// The data model behind the Checks stage.
//
// Everything the workspace knows about "is this analysis trustworthy, and is
// this design safe to present" is derived here, once, so the page, the results
// ticker, and any export all agree on the counts and the verdict.
//
// Three tiers, deliberately separated because they mean different things:
//
//   conflicts  – the census contradicts itself or is missing something the
//                model needed, so a number on screen may be wrong. Actionable.
//   screens    – the modeled compliance screens. Informational until one warns.
//   effects    – the plan working as written (eligibility exclusions, capped
//                after-tax). Outcomes, not problems, and never styled as alarms.
//
// Conflicts are grouped by PERSON, not by message: one employee who is both
// mis-flagged and short on hours is one card with two lines, not two rows in
// two different lists.

import { buildComplianceScreens, worstScreenTone } from './complianceScreens.js'

// Advisory notes that only restate an eligibility exclusion already shown in
// the effects tier. Filtered out so the same fact is never reported twice.
const DUPLICATE_ELIGIBILITY_ADVISORY_PATTERNS = [
  /^Not eligible to participate: requested employee deferrals were not modeled\.$/,
  /^Not eligible to participate: requested after-tax contributions were not modeled\.$/,
]
const isDuplicateEligibilityAdvisory = (warning) =>
  DUPLICATE_ELIGIBILITY_ADVISORY_PATTERNS.some((pattern) => pattern.test(warning))

// Census field that holds the answer for each eligibility requirement, so a
// "this is missing" conflict can send you to the exact cell instead of the row.
const REQUIREMENT_FIELD = { age: 'dob', service: 'hire_date', hours: 'hours_served' }

const REQUIREMENT_NOUN = { age: 'age', service: 'service', hours: 'hours' }

const displayName = (calculated, employeeIndex) =>
  calculated.name?.trim() || calculated.id || (employeeIndex >= 0 ? `Row ${employeeIndex + 1}` : 'Unknown row')

/**
 * One-click resolutions for a conflict, derived from the employee's actual data
 * and the plan's rules rather than from the warning text. A fix is only offered
 * when the correct edit is unambiguous; everything else gets a jump to the cell
 * so the person decides.
 *
 *   { kind: 'set',   field, value, label, detail }  – applies immediately
 *   { kind: 'focus', field, label }                 – opens that census cell
 */
function deriveFixes({ employee, calculated, eligibility }) {
  const fixes = []
  const requirements = calculated.eligibility?.requirements || {}
  const fullTimeFlag = String(employee?.full_time || '').trim().toUpperCase()

  // Marked full-time but under the plan's hours requirement. Exactly two things
  // can be true: the flag is wrong, or the hours are. Both are offered.
  if (eligibility.enabled && fullTimeFlag === 'Y' && requirements.hours === 'not_met') {
    fixes.push({
      kind: 'set',
      field: 'full_time',
      value: 'N',
      label: 'Set part-time',
      detail: 'Clears the contradiction and lets the long-term part-time rule apply if they qualify.',
    })
    fixes.push({ kind: 'focus', field: 'hours_served', label: 'Correct the hours' })
  }

  // Eligibility could not be determined because the census is missing the input
  // it depends on. These employees are modeled as full participants, which is
  // the optimistic read, so the cell is worth filling in.
  for (const [requirement, status] of Object.entries(requirements)) {
    if (status !== 'unknown') continue
    const field = REQUIREMENT_FIELD[requirement]
    if (!field) continue
    fixes.push({ kind: 'focus', field, label: `Add ${REQUIREMENT_NOUN[requirement]}` })
  }

  // Top-heavy cannot run without an explicit Y/N on every record.
  const keyStatus = String(employee?.key_employee || '').trim()
  if (keyStatus === '') {
    fixes.push({ kind: 'focus', field: 'key_employee', label: 'Set key status' })
  }

  // De-duplicate: two requirements can point at the same census cell.
  const seen = new Set()
  return fixes.filter((fix) => {
    const signature = `${fix.kind}:${fix.field}`
    if (seen.has(signature)) return false
    seen.add(signature)
    return true
  })
}

/**
 * Build the whole Checks model for one plan slot.
 *
 * @param {object}   plan       the plan design being checked
 * @param {object}   analysis   that plan's analysis slice (metrics,
 *                              calculatedEmployees, allocationDiagnostics)
 * @param {object[]} employees  the raw census, for input-row indexes
 */
export function buildChecksModel({ plan, analysis, employees = [] }) {
  const metrics = analysis?.metrics
  const calculatedEmployees = analysis?.calculatedEmployees || []
  const eligibility = plan?.eligibility || {}

  const indexById = new Map()
  employees.forEach((employee, index) => {
    if (!indexById.has(employee.id)) indexById.set(employee.id, index)
  })
  const employeeById = new Map(employees.map((employee) => [employee.id, employee]))

  // ---- Tier 1: conflicts, grouped by person -------------------------------
  const conflicts = calculatedEmployees
    .map((calculated) => {
      const notes = calculated.reviewWarnings || []
      if (notes.length === 0) return null
      const employeeIndex = indexById.has(calculated.id) ? indexById.get(calculated.id) : -1
      const employee = employeeById.get(calculated.id)
      return {
        id: calculated.id,
        label: displayName(calculated, employeeIndex),
        employeeIndex,
        compensation: calculated.compensation ?? null,
        group: calculated.allocationGroupLabel || null,
        notes,
        fixes: deriveFixes({ employee, calculated, eligibility }),
      }
    })
    .filter(Boolean)

  // ---- Tier 3a: eligibility, reported as a distribution -------------------
  // Who is limited: would-be savers whose requested deferrals are not modeled
  // because they fail age/service/hours, plus long-term part-time employees who
  // may defer but receive no employer money. Blank values resolve to "unknown"
  // and are modeled as full participants, so they surface as conflicts instead.
  const describeFailedRequirement = (key, calculated) => {
    if (key === 'age') return `age ${calculated.age ?? '–'} (plan requires ${eligibility.minAge})`
    if (key === 'service') return `${calculated.tenure?.totalMonths ?? '–'} mo service (plan requires ${eligibility.minServiceMonths})`
    if (key === 'hours') return `${calculated.hoursServed ?? '–'} hours (plan requires ${eligibility.minHours})`
    return key
  }

  const eligibilityLimited = !eligibility.enabled ? [] : calculatedEmployees
    .map((calculated) => {
      if (calculated.isTerminated) return null
      const isLtpt = calculated.eligibility?.status === 'ltpt'
      const blockedSaver = calculated.eligibility?.allowsDeferrals === false
        && ((calculated.desiredDeferral || 0) > 0 || (calculated.afterTaxRequested || 0) > 0)
      if (!isLtpt && !blockedSaver) return null
      const failed = Object.entries(calculated.eligibility?.requirements || {})
        .filter(([, status]) => status === 'not_met')
        .map(([requirement]) => requirement)
      if (failed.length === 0) return null
      const employeeIndex = indexById.has(calculated.id) ? indexById.get(calculated.id) : -1
      return {
        id: calculated.id,
        label: displayName(calculated, employeeIndex),
        employeeIndex,
        isLtpt,
        failed,
        details: failed.map((key) => describeFailedRequirement(key, calculated)).join('; '),
      }
    })
    .filter(Boolean)

  // Grouped by the rule that stopped them. A person failing two rules appears
  // under both, so the group counts intentionally sum to more than the headcount.
  const ruleLabel = {
    age: `Minimum age ${eligibility.minAge}`,
    service: `${eligibility.minServiceMonths} months of service`,
    hours: `${Number(eligibility.minHours || 0).toLocaleString()} hours`,
  }
  const eligibilityGroups = ['age', 'service', 'hours']
    .map((rule) => ({
      rule,
      label: ruleLabel[rule],
      people: eligibilityLimited.filter((entry) => entry.failed.includes(rule)),
    }))
    .filter((group) => group.people.length > 0)

  const eligibilityLimitedIds = new Set(eligibilityLimited.map((entry) => entry.id))

  // ---- Tier 3b: advisory notes --------------------------------------------
  const advisories = calculatedEmployees
    .map((calculated) => {
      const all = calculated.advisoryWarnings || []
      const notes = eligibilityLimitedIds.has(calculated.id)
        ? all.filter((warning) => !isDuplicateEligibilityAdvisory(warning))
        : all
      if (notes.length === 0) return null
      const employeeIndex = indexById.has(calculated.id) ? indexById.get(calculated.id) : -1
      return { id: calculated.id, label: displayName(calculated, employeeIndex), employeeIndex, notes }
    })
    .filter(Boolean)

  // ---- Tier 3c: allocation components -------------------------------------
  // Only worth a reader's attention when a pool did not fully allocate. An
  // all-zero or fully-allocated breakdown says nothing the results do not.
  const allocationComponents = analysis?.allocationDiagnostics || []
  const unallocatedComponents = allocationComponents.filter(
    (component) => Math.round(component.unallocatedResidual || 0) !== 0,
  )

  // ---- Tier 2: compliance screens -----------------------------------------
  const screens = buildComplianceScreens(metrics)
  const screenTone = worstScreenTone(screens)

  // ---- Verdict -------------------------------------------------------------
  // Conflicts outrank screens: a warning screen computed from contradictory
  // input is not a finding worth acting on yet.
  const verdict = conflicts.length > 0
    ? 'conflicts'
    : screenTone === 'good' ? 'clear' : 'attention'

  const planNotes = metrics?.groupAllocationWarnings || []

  return {
    verdict,
    screenTone,
    conflicts,
    screens,
    eligibility: {
      enabled: Boolean(eligibility.enabled),
      limited: eligibilityLimited,
      groups: eligibilityGroups,
      ltptCount: eligibilityLimited.filter((entry) => entry.isLtpt).length,
      eligibleCount: metrics?.eligibleEmployeeCount ?? null,
      ineligibleCount: metrics?.ineligibleEmployeeCount ?? null,
      totalCount: metrics?.totalEmployees ?? null,
    },
    advisories,
    planNotes,
    allocation: { components: allocationComponents, unallocated: unallocatedComponents },
    counts: {
      conflicts: conflicts.length,
      eligibilityLimited: eligibilityLimited.length,
      advisories: advisories.length,
      planNotes: planNotes.length,
      screensNeedingAttention: screens.filter((entry) => (
        entry.screen?.badge?.tone === 'warning' || entry.screen?.badge?.tone === 'danger'
      )).length,
    },
  }
}
